import { apiClient, logger } from '@/lib/local-platform';
import type {
  MemberVerifyResult,
  VerifyPasscodeReq,
  ResourceTopic,
  RedeemRequest,
  RedeemResult,
  RedemptionRecordListResp,
  GetRedemptionRecordsReq,
  MemberListResp,
  GetMemberListReq,
  CreateMemberReq,
  UpdateMemberReq,
  Member,
  MemberTypeConfigListResp,
  CreateMemberTypeConfigReq,
  UpdateMemberTypeConfigReq,
  CreateMemberTypeConfigResp,
  DeleteMemberTypeConfigResp,
  MemberAdminDetailResp,
  UpdateMemberResp,
  RedeemCodeBatchListResp,
  CreateRedeemCodeBatchReq,
  CreateRedeemCodeBatchResp,
  ExportRedeemCodesReq,
  ExportRedeemCodesResp,
  RevokeMemberReq,
  RevokeBatchUnusedResp,
  UpdateOrderNoReq,
  BindOrderNoReq,
  ImportOrderNosReq,
  ImportOrderNosResp,
  ParseOrderNoSheetResp,
  CreateQuickCodeReq,
  QuickCodeItem,
  QuickCodeListResp,
} from '@shared/api.interface';

function getAdminHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return token ? { 'x-admin-token': token } : {};
}

export async function verifyPasscode(data: VerifyPasscodeReq): Promise<MemberVerifyResult> {
  try {
    const response = await apiClient({
      url: '/api/members/verify',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error) {
    logger.error('验证兑换码失败', error);
    throw error;
  }
}

export async function getActiveTopics(): Promise<ResourceTopic[]> {
  try {
    const response = await apiClient({
      url: '/api/members/topics',
      method: 'GET',
    });
    return response.data;
  } catch (error) {
    logger.error('获取主题列表失败', error);
    throw error;
  }
}

export async function redeemResource(data: RedeemRequest): Promise<RedeemResult> {
  try {
    const response = await apiClient({
      url: '/api/members/redeem',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (!status || status >= 500) {
      logger.error('领取资源失败', error);
    }
    throw error;
  }
}

export async function trackTopicView(topicName: string): Promise<void> {
  try {
    await apiClient({
      url: `/api/members/topics/${encodeURIComponent(topicName)}/view`,
      method: 'POST',
    });
  } catch (error) {
    logger.error('记录主题浏览次数失败', error);
  }
}

export async function getRedemptions(params: GetRedemptionRecordsReq): Promise<RedemptionRecordListResp> {
  try {
    const response = await apiClient({
      url: '/api/members/redemptions',
      method: 'GET',
      params,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取领取记录失败', error);
    throw error;
  }
}

export async function getMembers(params: GetMemberListReq): Promise<MemberListResp> {
  try {
    const response = await apiClient({
      url: '/api/members',
      method: 'GET',
      params,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取会员列表失败', error);
    throw error;
  }
}

export async function getMemberByPasscode(passcode: string): Promise<Member | null> {
  try {
    const response = await apiClient({
      url: '/api/members/detail',
      method: 'GET',
      params: { passcode },
    });
    return response.data;
  } catch (error) {
    logger.error('获取会员详情失败', error);
    throw error;
  }
}

export async function getAdminMemberDetail(passcode: string): Promise<MemberAdminDetailResp> {
  try {
    const response = await apiClient({
      url: '/api/members/admin/detail',
      method: 'GET',
      params: { passcode },
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取管理员会员详情失败', error);
    throw error;
  }
}

export async function createMember(data: CreateMemberReq): Promise<void> {
  try {
    await apiClient({
      url: '/api/members',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
  } catch (error) {
    logger.error('创建会员失败', error);
    throw error;
  }
}

export async function updateMember(data: { passcode: string } & UpdateMemberReq): Promise<UpdateMemberResp> {
  try {
    const response = await apiClient({
      url: '/api/members/update',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('更新会员失败', error);
    throw error;
  }
}

export async function getMemberTypeConfigs(): Promise<MemberTypeConfigListResp> {
  try {
    const response = await apiClient({
      url: '/api/members/type-configs',
      method: 'GET',
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取会员类型配置失败', error);
    throw error;
  }
}

export async function createMemberTypeConfig(data: CreateMemberTypeConfigReq): Promise<CreateMemberTypeConfigResp> {
  try {
    const response = await apiClient({
      url: '/api/members/type-configs',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('创建会员类型配置失败', error);
    throw error;
  }
}

export async function updateMemberTypeConfig(id: string, data: UpdateMemberTypeConfigReq): Promise<void> {
  try {
    await apiClient({
      url: `/api/members/type-configs/${id}`,
      method: 'PUT',
      data,
      headers: getAdminHeaders(),
    });
  } catch (error) {
    logger.error('更新会员类型配置失败', error);
    throw error;
  }
}

export async function deleteMemberTypeConfig(id: string): Promise<DeleteMemberTypeConfigResp> {
  try {
    const response = await apiClient({
      url: `/api/members/type-configs/${id}`,
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('删除会员类型配置失败', error);
    throw error;
  }
}

// ========== 批量兑换码 ==========

async function adminRequest<T>(url: string, method: 'GET' | 'POST', label: string, data?: unknown): Promise<T> {
  try {
    const response = await apiClient({ url, method, data, headers: getAdminHeaders() });
    return response.data;
  } catch (error) {
    logger.error(label, error);
    throw error;
  }
}

export function listRedeemCodeBatches(): Promise<RedeemCodeBatchListResp> {
  return adminRequest('/api/members/batches', 'GET', '获取兑换码批次失败');
}

export function createRedeemCodeBatch(data: CreateRedeemCodeBatchReq): Promise<CreateRedeemCodeBatchResp> {
  return adminRequest('/api/members/batches', 'POST', '生成兑换码失败', data);
}

export function exportRedeemCodes(batchId: number, data: ExportRedeemCodesReq): Promise<ExportRedeemCodesResp> {
  return adminRequest(`/api/members/batches/${batchId}/export`, 'POST', '导出兑换码失败', data);
}

export function revokeBatchUnused(batchId: number): Promise<RevokeBatchUnusedResp> {
  return adminRequest(`/api/members/batches/${batchId}/revoke-unused`, 'POST', '批次作废失败', {});
}

export function revokeMember(data: RevokeMemberReq): Promise<{ success: boolean }> {
  return adminRequest('/api/members/revoke', 'POST', '作废兑换码失败', data);
}

export function updateOrderNo(data: UpdateOrderNoReq): Promise<{ success: boolean }> {
  return adminRequest('/api/members/order-no', 'POST', '保存订单号失败', data);
}

export function listQuickCodes(): Promise<QuickCodeListResp> {
  return adminRequest('/api/members/quick-codes', 'GET', '获取最近发出的码失败');
}

export function createQuickCode(data: CreateQuickCodeReq): Promise<QuickCodeItem> {
  return adminRequest('/api/members/quick-codes', 'POST', '快捷发码失败', data);
}

export async function downloadOrderNoTemplate(): Promise<Blob> {
  const response = await apiClient({
    url: '/api/members/order-nos/template',
    method: 'GET',
    responseType: 'blob',
    headers: getAdminHeaders(),
  });
  return response.data;
}

export function parseOrderNoSheet(file: File): Promise<ParseOrderNoSheetResp> {
  const formData = new FormData();
  formData.append('file', file);
  return adminRequest('/api/members/order-nos/parse', 'POST', '读取发货记录失败', formData);
}

export function importOrderNos(data: ImportOrderNosReq): Promise<ImportOrderNosResp> {
  return adminRequest('/api/members/order-nos/import', 'POST', '导入发货记录失败', data);
}

// 用户首次使用批量码后选填订单号（不需要管理员登录）
export async function bindOrderNo(data: BindOrderNoReq): Promise<{ success: boolean }> {
  const response = await apiClient({ url: '/api/members/bind-order-no', method: 'POST', data });
  return response.data;
}
